import { isConfigEnabled } from './isConfigEnabled';

const DEFAULT_TIER_AUTO_RULES = [
    {
        id: 'capital-below-threshold',
        isEnable: true,
        conditions: [
            { field: 'gcmTeam', operator: 'equals', value: 'Capital' },
            { field: 'dealValue', operator: 'lt', value: 25000000 },
        ],
        result: null,
        belowThreshold: true,
    },
    {
        id: 'capital-tier-2',
        isEnable: true,
        conditions: [
            { field: 'gcmTeam', operator: 'equals', value: 'Capital' },
            { field: 'dealValue', operator: 'gte', value: 25000000 },
        ],
        result: 'Tier 2',
    },
    {
        id: 'idm-below-threshold',
        isEnable: true,
        conditions: [
            { field: 'gcmTeam', operator: 'equals', value: 'IDM' },
            { field: 'dealValue', operator: 'lt', value: 5000000 },
        ],
        result: null,
        belowThreshold: true,
    },
    {
        id: 'idm-tier-1',
        isEnable: true,
        conditions: [
            { field: 'gcmTeam', operator: 'equals', value: 'IDM' },
            { field: 'dealValue', operator: 'gte', value: 5000000 },
            { field: 'dealValue', operator: 'lte', value: 25000000 },
        ],
        result: 'Tier 1',
    },
    {
        id: 'idm-tier-2',
        isEnable: true,
        conditions: [
            { field: 'gcmTeam', operator: 'equals', value: 'IDM' },
            { field: 'dealValue', operator: 'gt', value: 25000000 },
        ],
        result: 'Tier 2',
    },
];

/**
 * Maps new-style condition operators to legacy tier-reset operator names.
 */
const mapResetOperator = (operator) => {
    if (operator === 'hasValue') {
        return 'is_there';
    }

    if (operator === 'anyOf') {
        return 'any_of';
    }

    if (operator === 'equals') {
        return 'is_equals';
    }

    return operator;
};

/**
 * Converts behavior.tier.reset.rules into legacy TIER_RESET_CONFIG shape.
 */
const convertTierResetRulesToLegacy = (tierConfig = {}) => {
    const controlField = tierConfig.controlField || 'topTierRequired';
    const rules = tierConfig.reset?.rules || [];

    return rules
        .filter((rule) => isConfigEnabled(rule))
        .map((rule) => ({
            selectedConditionDataElement: controlField,
            selectedConditionOperator: 'is_equals',
            value: rule.tier,
            actionButtonShowFields: (rule.showButtonWhen || []).map((condition) => ({
                selectedConditionDataElement: condition.field,
                selectedConditionOperator: mapResetOperator(condition.operator),
                value: condition.value,
            })),
            onClickResetButtonResetFields: rule.resetFields || [],
        }));
};

/**
 * Resolves legacy tier-reset config from chartData root keys.
 */
const resolveLegacyTierResetConfig = (chartData = {}) => {
    const candidates = [
        chartData.tierResetConfig,
        chartData.TIER_RESET_CONFIG,
        chartData.tabs?.dealReviewDetails?.TIER_RESET_CONFIG,
    ];

    return candidates.find((candidate) => Array.isArray(candidate) && candidate.length) || [];
};

/**
 * Deep-merges behavior config with defaults and resolves legacy chartData keys.
 */
export const resolveBehaviorConfig = (chartData = {}) => {
    const rawBehavior = chartData.behavior || {};
    const tierRaw = rawBehavior.tier || {};
    const hasBehaviorBlock = Boolean(chartData.behavior);
    const behaviorMasterEnabled = isConfigEnabled(rawBehavior, true);
    const legacyIdmEnabled = chartData.isIDMEnable !== false;
    const tierDefaultEnabled = hasBehaviorBlock ? behaviorMasterEnabled : legacyIdmEnabled;

    const tierConfig = {
        isEnable: isConfigEnabled(tierRaw, tierDefaultEnabled),
        controlField: tierRaw.controlField || 'topTierRequired',
        controlFieldReadOnly: tierRaw.controlFieldReadOnly !== false,
        namePattern: tierRaw.namePattern || 'Tier {n}',
        autoCalculation: {
            isEnable: isConfigEnabled(tierRaw.autoCalculation, tierDefaultEnabled),
            triggerFields: tierRaw.autoCalculation?.triggerFields || ['gcmTeam', 'dealValue'],
            skipControlFieldUpdateWhenResultIsNull: tierRaw.autoCalculation?.skipControlFieldUpdateWhenResultIsNull !== false,
            rules: tierRaw.autoCalculation?.rules?.length
                ? tierRaw.autoCalculation.rules
                : DEFAULT_TIER_AUTO_RULES,
            belowThreshold: {
                isEnable: isConfigEnabled(tierRaw.autoCalculation?.belowThreshold, true),
                blockSave: tierRaw.autoCalculation?.belowThreshold?.blockSave !== false,
                feedbackType: tierRaw.autoCalculation?.belowThreshold?.feedbackType || 'toast',
                messageField: tierRaw.autoCalculation?.belowThreshold?.messageField || 'gcmTeam',
                messages: tierRaw.autoCalculation?.belowThreshold?.messages || {
                    Capital: {
                        title: 'Below tracker threshold',
                        message: 'Capital-managed deals under $25M are not tracked in dSilo. Use the standard Nego Plan / Exec Summary process instead.',
                    },
                    IDM: {
                        title: 'Below tracker threshold',
                        message: 'IDM-managed deals under $5M are not tracked in dSilo. Use the standard Nego Plan / Exec Summary process instead.',
                    },
                },
            },
        },
        editability: {
            isEnable: isConfigEnabled(tierRaw.editability, tierDefaultEnabled),
            strategy: tierRaw.editability?.strategy || 'exactTierMatch',
            controlFieldEditable: tierRaw.editability?.controlFieldEditable === true,
            messages: {
                noTopTierSet: tierRaw.editability?.messages?.noTopTierSet
                    || '{fieldLabel} is not editable until top tier is set.',
                wrongTier: tierRaw.editability?.messages?.wrongTier
                    || '{fieldLabel} is only editable when top tier is Tier {tierLevel}.',
                generic: tierRaw.editability?.messages?.generic
                    || '{fieldLabel} is not editable for the current top tier.',
            },
        },
        clearOnChange: {
            isEnable: isConfigEnabled(tierRaw.clearOnChange, true),
            clearHistoryFields: tierRaw.clearOnChange?.clearHistoryFields !== false,
            historyFieldSuffix: tierRaw.clearOnChange?.historyFieldSuffix || 'History',
        },
        dateValidation: {
            isEnable: isConfigEnabled(tierRaw.dateValidation, true),
            mode: tierRaw.dateValidation?.mode === 'error' ? 'error' : 'warning',
            feedbackType: tierRaw.dateValidation?.feedbackType || 'inline',
            rules: tierRaw.dateValidation?.rules || [],
        },
        reset: {
            isEnable: isConfigEnabled(tierRaw.reset, true),
            successMessage: tierRaw.reset?.successMessage || 'Deal Reset Completed',
            rules: tierRaw.reset?.rules || [],
        },
    };

    const negotiationRaw = rawBehavior.negotiationStatus || {};
    const negotiationStatus = {
        isEnable: isConfigEnabled(negotiationRaw, behaviorMasterEnabled),
        field: negotiationRaw.field || 'negotiationStatus',
        approvalDateField: negotiationRaw.approvalDateField || 'approvalDate',
        topTierField: negotiationRaw.topTierField || tierConfig.controlField,
        options: negotiationRaw.options || [
            'Deal Review',
            'New Negotiation',
            'Negotiation In Progress',
            'Negotiation Completed',
            'Negotiation Cancelled',
        ],
        autoUpdate: {
            isEnable: isConfigEnabled(negotiationRaw.autoUpdate, behaviorMasterEnabled),
            skipWhenCurrentValueIn: negotiationRaw.autoUpdate?.skipWhenCurrentValueIn || ['Negotiation Cancelled'],
            skipWhenSavingField: negotiationRaw.autoUpdate?.skipWhenSavingField || 'negotiationStatus',
            rules: negotiationRaw.autoUpdate?.rules || [
                {
                    id: 'completed',
                    isEnable: true,
                    condition: { field: 'approvalDate', operator: 'hasValue' },
                    set: 'Negotiation Completed',
                },
                {
                    id: 'in-progress',
                    isEnable: true,
                    condition: { type: 'relevantTierApprovalDate', operator: 'hasValue' },
                    set: 'Negotiation In Progress',
                },
                {
                    id: 'default',
                    isEnable: true,
                    condition: { operator: 'default' },
                    set: 'Deal Review',
                },
            ],
            tierApprovalDateFields: negotiationRaw.autoUpdate?.tierApprovalDateFields || {
                0: 'tier0ApprovalDate',
                1: 'tier1ApprovalDate',
                2: 'tier2ApprovalDate',
            },
            triggerFields: {
                always: negotiationRaw.autoUpdate?.triggerFields?.always || ['approvalDate', 'topTierRequired'],
                includeRelevantTierApprovalDate: negotiationRaw.autoUpdate?.triggerFields?.includeRelevantTierApprovalDate !== false,
            },
        },
        manualValidation: {
            isEnable: isConfigEnabled(negotiationRaw.manualValidation, false),
            feedbackType: negotiationRaw.manualValidation?.feedbackType || 'toast',
            blockSave: negotiationRaw.manualValidation?.blockSave !== false,
            rules: negotiationRaw.manualValidation?.rules || [],
        },
    };

    const featuresRaw = rawBehavior.features || {};
    const tableRaw = rawBehavior.table || {};
    const loaderRaw = tableRaw.loader || {};
    const loaderShowOn = loaderRaw.showOn || {};
    const saveFeedbackRaw = tableRaw.saveFeedback || {};
    const apiRaw = rawBehavior.api || {};

    const tierResetLegacy = tierConfig.reset.rules.length
        ? convertTierResetRulesToLegacy(tierConfig)
        : resolveLegacyTierResetConfig(chartData);

    return {
        isEnable: isConfigEnabled(rawBehavior, true),
        features: {
            inlineEdit: { isEnable: isConfigEnabled(featuresRaw.inlineEdit, true) },
            rowSelection: { isEnable: isConfigEnabled(featuresRaw.rowSelection, false) },
            fieldHistory: { isEnable: isConfigEnabled(featuresRaw.fieldHistory, true) },
            silentRefreshOnSave: { isEnable: isConfigEnabled(featuresRaw.silentRefreshOnSave, true) },
            optimisticRowUpdateOnSave: { isEnable: isConfigEnabled(featuresRaw.optimisticRowUpdateOnSave, true) },
        },
        api: {
            isEnable: isConfigEnabled(apiRaw, true),
            idm: { isEnable: isConfigEnabled(apiRaw.idm, legacyIdmEnabled) },
            historyDataModelId: apiRaw.historyDataModelId
                || chartData.historyDataModelId
                || chartData.tabs?.activities?.dataModelId
                || chartData.selectedDataModel,
            subDataModelId: apiRaw.subDataModelId
                || chartData.subDataModelId
                || chartData.historySubDataModelId
                || chartData.tabs?.activities?.subDataModelId
                || chartData.selectedDataModel,
        },
        table: {
            isEnable: isConfigEnabled(tableRaw, true),
            minColumnWidth: tableRaw.minColumnWidth || 200,
            loader: {
                isEnable: isConfigEnabled(loaderRaw, true),
                showOn: {
                    initialLoad: loaderShowOn.initialLoad !== false,
                    filterChange: loaderShowOn.filterChange !== false,
                    pagination: loaderShowOn.pagination !== false,
                    sortChange: loaderShowOn.sortChange !== false,
                    rowsPerPageChange: loaderShowOn.rowsPerPageChange !== false,
                    cellSave: loaderShowOn.cellSave === true,
                    tierReset: loaderShowOn.tierReset === true,
                },
            },
            saveFeedback: {
                isEnable: isConfigEnabled(saveFeedbackRaw, true),
                successToast: {
                    isEnable: isConfigEnabled(saveFeedbackRaw.successToast, true),
                    message: saveFeedbackRaw.successToast?.message || 'Record updated successfully',
                },
                errorToast: {
                    isEnable: isConfigEnabled(saveFeedbackRaw.errorToast, true),
                    message: saveFeedbackRaw.errorToast?.message || 'Failed to update record',
                },
            },
        },
        tier: tierConfig,
        negotiationStatus,
        validation: {
            isEnable: isConfigEnabled(rawBehavior.validation, true),
            required: {
                isEnable: isConfigEnabled(rawBehavior.validation?.required, true),
                feedbackType: rawBehavior.validation?.required?.feedbackType || 'toast',
            },
            nonEditable: {
                isEnable: isConfigEnabled(rawBehavior.validation?.nonEditable, true),
                feedbackType: rawBehavior.validation?.nonEditable?.feedbackType || 'toast',
                message: rawBehavior.validation?.nonEditable?.message
                    || 'You cannot modify {fieldLabel} because it is not editable.',
            },
        },
        tierResetLegacy: isConfigEnabled(tierConfig.reset) ? tierResetLegacy : [],
    };
};

/**
 * Returns whether IDM API mode is active from resolved behavior config.
 */
export const isIdmApiEnabled = (behaviorConfig) => (
    isConfigEnabled(behaviorConfig)
    && isConfigEnabled(behaviorConfig.api)
    && isConfigEnabled(behaviorConfig.api.idm)
);

/**
 * Resolves loader visibility for a table fetch trigger.
 */
export const shouldShowTableLoader = (behaviorConfig, trigger) => {
    if (!isConfigEnabled(behaviorConfig?.table) || !isConfigEnabled(behaviorConfig?.table?.loader)) {
        return true;
    }

    return behaviorConfig.table.loader.showOn[trigger] !== false;
};
